import { getDistance2D } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import { IDisposable } from 'cd-eft/framework/Disposer';
import { action, IReactionDisposer, reaction } from 'mobx';
import { XyzPoint } from '../../SurveyEngineering/XyzPoint';
import { DynamicProfileLayerType, ProfileKeyCode, SketchState } from './DynamicProfileEnums';
import { DynamicProfileHubBase } from './DynamicProfileHubBase';
import stores from 'cd-eft/models/Stores';

const { alignmentDesignStore } = stores.AppStore;
abstract class DynamicTableStoreBase implements IDisposable {
  protected hub: DynamicProfileHubBase | undefined;

  public currentObject;

  private mapSketchReactionDisposer: IReactionDisposer;
  private newVertexReactionDisposer: IReactionDisposer;
  private insertVertexReactionDisposer: IReactionDisposer;
  private moveVertexReactionDisposer: IReactionDisposer;
  private sketchCancelReactionDisposer: IReactionDisposer;
  private profileSketchReactionDisposer: IReactionDisposer;
  private profileSketchCancelReactionDisposer: IReactionDisposer;
  private newProfileVertexReactionDisposer: IReactionDisposer;
  private moveProfileVertexReactionDisposer: IReactionDisposer;
  private insertProfileVertexReactionDisposer: IReactionDisposer;

  protected precision = 4;

  protected profileKeys: ProfileKeyCode[] = [ProfileKeyCode.DEFAULT];

  public focusStationIndex = -1;

  constructor() {
    // communication pattern 1: broadcast/observer pattern
    this.mapSketchReactionDisposer = reaction(
      () => this.hub?.MapSketchState.state === SketchState.START,
      (isStarted) => {
        if (isStarted) this.reset();
      }
    );

    this.newVertexReactionDisposer = reaction(
      () => this.hub?.NewMapVertexInfo,
      async (info) => {
        if (info) await this.addRow(info.vertex, info.state, info.vertexIndex, info.originalVertex, info.blockTable);
      }
    );

    this.insertVertexReactionDisposer = reaction(
      () => this.hub?.InsertMapVertexInfo,
      async (info) => {
        if (info) await this.insertRow(info.newVertex, info.vertexIndex, info.blockTable);
      }
    );

    this.moveVertexReactionDisposer = reaction(
      () => this.hub?.MoveMapVertexInfo,
      async (info) => {
        if (info) await this.modifyRow(info.newVertex, info.vertexIndex);
      }
    );

    this.sketchCancelReactionDisposer = reaction(
      () => this.hub?.MapSketchState.state === SketchState.CANCEL,
      (isCanceled) => {
        if (isCanceled) this.reset();
      }
    );

    this.profileSketchReactionDisposer = reaction(
      () => this.hub?.ProfileSketchState.state === SketchState.START,
      (isStarted) => {
        if (isStarted) this.reset();
      }
    );

    this.profileSketchCancelReactionDisposer = reaction(
      () => this.hub?.ProfileSketchState.state === SketchState.CANCEL,
      (isCanceled) => {
        if (isCanceled) this.reset();
      }
    );

    this.newProfileVertexReactionDisposer = reaction(
      () => this.hub?.NewProfileVertexInfo,
      async (info) => {
        if (info) await this.addRows(info);
      }
    );
    this.insertProfileVertexReactionDisposer = reaction(
      () => this.hub?.InsertProfileVertexInfo,
      async (info) => {
        if (info) await this.insertRows(info.newVertex, info.vertexIndex, info.keyCode, info.addWDTEdits, info.addTerraceEdits);
      }
    );
    this.moveProfileVertexReactionDisposer = reaction(
      () => this.hub?.MoveProfileVertexInfo,
      async (info) => {
        if (info) await this.modifyRows(info.newVertex, info.vertexIndex, info.addWDTEdits);
      }
    );
  }

  protected abstract reset();

  protected abstract insertRow(newVertex: any, vertexIndex: any, originalVertex?: XyzPoint, blockTable?: boolean);

  protected abstract addRow(vertex: XyzPoint, state: string, vertexIndex: number, originalVertex?: XyzPoint, blockTable?: boolean);

  protected abstract addRows(info: any);
  protected abstract insertRows(newVertex: any, vertexIndex: any, keyCode: ProfileKeyCode, addWDTEdits?: any,addTerraceEdits?: any);

  protected abstract modifyRows(info: any, vertexIndex: number, addWDTEdits?: any, storeEdits?: boolean);

  /** setters and getters */
  set CurrentObject(currentObject) {
    if (!this.currentObject || this.currentObject.designElementId !== currentObject.designElementId) {
      this.currentObject = currentObject;
      this.LoadAlignmentToMap(this.currentObject);
    }
  }

  completeMapSketch() {
    this.hub?.completeMapSketch();
  }

  LoadAlignmentToMap(data: any) {
    this.hub?.LoadEditToMap(data, this.profileKeys[0]);
    this.hub?.MapStore?.generateAlignmentLabelsLayer();

    if (!this.hub?._isTerraceAlignment) {
      this.profileKeys.forEach((keyCode) => {
      this.hub?.LoadProfile(data, keyCode);
      });
    }
    alignmentDesignStore.disableSaveAlignment = true;
  }

  DrawCurve(data: any) {
    this.hub?.DrawCurve(data);
  }

  drawCurvedProfile(data: any) {
    this.hub?.drawCurvedProfile(data, this.profileKeys[0]);

  }

  cleanUpMap() {
    this.hub?.cleanUpMap();
  }

  /** public methods */
  // communication pattern 2: delegation pattern
  sketchPolyline() {
    this.hub?.SketchPolyline();
  }

  toggleAlignmentSnapping() {
    this.hub?.toggleAlignmentSnapping();
  }

  insertStation(applyDefaultCurves?) {
    this.hub?.InsertVertex(applyDefaultCurves);
  }

  moveStation(applyDefaultCurves?) {
    this.hub?.MoveVertex(applyDefaultCurves);
  }

  DeleteStation(indexToDelete: number, alignmentObject, blockModifyProfile = false, fromTerrace, tableStore) {
    this.broadcastVertexToDelete(indexToDelete, this.profileKeys[0], alignmentObject, blockModifyProfile, fromTerrace, tableStore);

    this.calculateStationDistances();

    if (this.currentObject.alignmentStations.length === 0) this.reset();
  }

  async redrawAlignmentProfile() {
    await this.hub?.redrawAlignmentProfile();
  }

  async checkToConvertAlignment(index, fromDeletion) {
    await this.hub?.checkToConvertAlignment(index, fromDeletion);
  }

  protected abstract calculateStationDistances();

  @action
  protected async modifyRow(dataPoint: XyzPoint, vertexIndex: number) {
    const stations = this.currentObject.alignmentStations;
    if (vertexIndex < 0 || stations.length <= vertexIndex) return;

    stations[vertexIndex].x = dataPoint.getX().toFixed(this.precision);
    stations[vertexIndex].y = dataPoint.getY().toFixed(this.precision);
    stations[vertexIndex].z = dataPoint.getZ()?.toFixed(this.precision);

    if (stations.length >= 2) {
      let p1 = new XyzPoint(stations[0].x, stations[0].y, 0);
      let dist = 0;
      for (let i = 1; i < stations.length; i++) {
        const element = stations[i];
        const p2 = new XyzPoint(element.x, element.y, 0);
        dist += getDistance2D(p1, p2);
        stations[i].stationDistance = dist.toFixed(this.precision);
        stations[i].stationName = Math.round(dist);
        p1 = p2;
      }
    }
    alignmentDesignStore.disableSaveAlignment = false;
  }

  public formatWaterwayStation(input: string): string {
    if (!isNaN(parseFloat(input)) && isFinite(parseFloat(input))) {

      // Split the string into integer and decimal parts
      const [integerPart, decimalPart] = input.split('.');
      const quotient: number = Math.floor(parseInt(integerPart) / 100);
      const remainder: number = parseInt(integerPart) % 100;

      // Combine the quotient and remainder
      // const result: string = quotient.toString() + '+' + ((remainder >= 0 && remainder < 10) ? ('0' + remainder.toString()) : remainder.toString());
      const twoDigits = (remainder >= 0 && remainder < 10) ? ('0' + remainder.toString()) : remainder.toString();
      const result: string = decimalPart ? `${quotient}+${twoDigits}.${decimalPart}` : `${quotient}+${twoDigits}`;
      return result;
    }
    return '';
  }

 public constructWaterwayStation(input: string): number {
    if (input) {
      // Split the string into integer and decimal parts
      const [firstPart, lastPart] = input.split('+').map(part => part.trim());
      // Convert firstPart to a number, default to 0 if not valid
      const firstPartValue: number = parseInt(firstPart, 10) || 0;
      // Convert lastPart to a number, default to 0 if not valid
      const lastPartValue: number = parseFloat(lastPart) || 0;
      const result: number = (firstPartValue * 100) + Math.round(lastPartValue * 10) / 10;
      return result;
    }
    return 0;
  }

  @action
  private broadcastVertexToDelete(index: number, keyCode: ProfileKeyCode, alignmentObject, blockModifyProfile = false, fromTerrace, tableStore) {
    if (this.hub) this.hub.TableBorneVertexIndexToDeleteInfo = { index, keyCode, alignmentObject, blockModifyProfile, fromTerrace, tableStore };
  }

  // @action
  // protected broadcastGuardrails(start: number, end: number): void {
  //   this.hub?.NextGuardrailInfo({ start, end });
  // }

  /** delegations */
  removeMapLayer(layer: DynamicProfileLayerType) {
    this.hub?.removeMapLayer(layer);
  }

  /** dispose */
  Dispose() {
    if (this.mapSketchReactionDisposer) this.mapSketchReactionDisposer();
    if (this.newVertexReactionDisposer) this.newVertexReactionDisposer();
    if (this.insertVertexReactionDisposer) this.insertVertexReactionDisposer();
    if (this.moveVertexReactionDisposer) this.moveVertexReactionDisposer();
    if (this.sketchCancelReactionDisposer) this.sketchCancelReactionDisposer();
    if (this.profileSketchReactionDisposer) this.profileSketchReactionDisposer();
    if (this.profileSketchCancelReactionDisposer) this.profileSketchCancelReactionDisposer();
    if (this.newProfileVertexReactionDisposer) this.newProfileVertexReactionDisposer();
    if (this.moveProfileVertexReactionDisposer) this.moveProfileVertexReactionDisposer();
    if (this.insertProfileVertexReactionDisposer) this.insertProfileVertexReactionDisposer();

    this.focusStationIndex = -1;
  }
}

export { DynamicTableStoreBase };
